import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { generateDataTestId } from '../../utils';

interface CreateAccordionProps {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  padding?: number;
  maxWidth?: string;
  subtext?: React.ReactNode;
}

const FormAccordion: React.FC<CreateAccordionProps> = ({
  title,
  children,
  defaultExpanded = true,
  padding = 3,
  maxWidth,
  subtext,
}) => {
  return (
    <Accordion
      data-testid={generateDataTestId(
        typeof title === 'string' ? title : 'undefined',
        undefined,
        'accordion',
      )}
      defaultExpanded={defaultExpanded}
      disableGutters
      sx={{
        width: '100%',
        maxWidth: maxWidth,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        mb: 2,
        '&:before': {
          display: 'none',
        },
        '& .MuiCollapse-root': {
          margin: 0,
        },
        // Using !important because the underlying global CSS library is applying
        // styles with !important. This override will be removed once the global
        // styles are updated.
        '& .MuiAccordionSummary-root': {
          bgcolor: (theme) => `${theme.palette.background.paper} !important`, // implicit string does not work here, use theme
          padding: 3,
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{
              color: 'grey.700', // Make icon darker using theme colors
              fontSize: '1.5rem', // Make icon larger
              fontWeight: 'bold', // Attempt to make bolder
            }}
          />
        }
        aria-controls={`${typeof title === 'string' ? title : 'panel'}-content`}
        id={`${typeof title === 'string' ? title : 'panel'}-header`}
        sx={{
          // Using !important to override global CSS styles, will be removed
          // when the global stylesheet is updated
          bgcolor: 'background.paper !important',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          px: 3,
          py: 1,
          '& .MuiAccordionSummary-content': {
            my: 1,
            flexDirection: 'column',
          },
          // Make the expand icon bolder and darker
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: 'grey.700',
            '& svg': {
              width: 28,
              height: 28,
              stroke: 'currentColor',
              strokeWidth: 2, // Increase stroke width for a bolder appearance
            },
          },
        }}
      >
        <Box>
          {typeof title === 'string' ? (
            <Typography variant="h4">{title}</Typography>
          ) : (
            title
          )}
          {subtext && (
            <Typography variant="body2" sx={{ mt: 2, mb: 0 }}>
              {subtext}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          p: padding,
          // Using !important to override global CSS styles that will be removed
          bgcolor: 'background.paper !important',
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default FormAccordion;
